import { useEffect, useState, useMemo, useCallback } from "react";
import { CustomListFieldService } from "../Services/CustomLifFieldService";
import { CustomListItemService } from "../Services/CustomListItemService";
import { WebApiCustomListsDtoCustomListItemDto, WebApiModelsCustomListField } from "../Repository/eventbookyapi";

export function useCustomListLookupData(memberFieldSettings: any) {
    const [customFieldsMap, setCustomFieldsMap] = useState<Record<number, WebApiModelsCustomListField[]>>({});
    const [lookupOptionsMap, setLookupOptionsMap] = useState<Record<number, Record<string, { value: number; label: string }[]>>>({});
    const [loading, setLoading] = useState(false);

    // Memoize lookup fields calculation to prevent recalculation on every render
    const lookupFields = useMemo(() =>
        (memberFieldSettings || []).filter((f: any) => f.uiControl === 4 && f.customListId),
        [memberFieldSettings]
    );

    // Helper function to process items data
    const processItems = useCallback((items: WebApiCustomListsDtoCustomListItemDto[]) => {
        const optionsMap: Record<string, { value: number; label: string }[]> = { default: [] };

        (items || []).forEach((item: WebApiCustomListsDtoCustomListItemDto) => {
            let label = item.id?.toString() || '';
            if (Array.isArray(item.fieldValues) && item.fieldValues.length > 0) {
                label = item.fieldValues[0].fieldValue;
            }
            optionsMap.default.push({ value: item.id!, label });
        });

        return optionsMap;
    }, []);

    useEffect(() => {
        const fetchAll = async () => {
            if (!lookupFields.length) return;

            try {
                setLoading(true);

                // Get unique custom list IDs to avoid duplicate API calls
                const customListIds = lookupFields.map((f: any) => f.customListId);
                const uniqueCustomListIds = customListIds.filter((id: number, index: number) =>
                    customListIds.indexOf(id) === index
                );

                // Filter out already loaded data
                const fieldsToFetch = uniqueCustomListIds.filter((id: number) => !customFieldsMap[id]);
                const itemsToFetch = uniqueCustomListIds.filter((id: number) => !lookupOptionsMap[id]);

                // Parallel API calls for better performance
                const [fieldsResults, itemsResults] = await Promise.all([
                    Promise.all(fieldsToFetch.map((id: number) =>
                        CustomListFieldService.GetCCustomListFields(id)
                            .then(fields => ({ id, fields: fields as WebApiModelsCustomListField[] }))
                    )),
                    Promise.all(itemsToFetch.map((id: number) =>
                        CustomListItemService.GetItems(id)
                            .then(items => ({ id, items: items as WebApiCustomListsDtoCustomListItemDto[] }))
                    ))
                ]);

                // Batch state updates to prevent multiple re-renders
                if (fieldsResults.length > 0) {
                    const newFieldsMap: Record<number, WebApiModelsCustomListField[]> = {};
                    fieldsResults.forEach(({ id, fields }) => {
                        newFieldsMap[id] = fields;
                    });
                    setCustomFieldsMap(prev => ({ ...prev, ...newFieldsMap }));
                }

                if (itemsResults.length > 0) {
                    const newOptionsMap: Record<number, Record<string, { value: number; label: string }[]>> = {};
                    itemsResults.forEach(({ id, items }) => {
                        newOptionsMap[id] = processItems(items);
                    });
                    setLookupOptionsMap(prev => ({ ...prev, ...newOptionsMap }));
                }

            } catch (error) {
                console.error('Error fetching lookup data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAll();
    }, [lookupFields, customFieldsMap, lookupOptionsMap, processItems]);

    return {
        customFieldsMap,
        lookupOptionsMap,
        loading,
        isReady: lookupFields.every((field: any) =>
            customFieldsMap[field.customListId] && lookupOptionsMap[field.customListId]
        )
    };
}
